import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryParams } from '../../../hooks/UseQueryParams';
import { useBrowserStorage } from '../../../hooks/UseBrowserStorage';

import type { UseGalleryOnPageArgs } from './GalleryOnPage.hook.typed';
import type { GalleryOpenData, GalleryUsageData } from './types/galleryTypes';

const useGalleryOnPage = (props: UseGalleryOnPageArgs) => {
  const { reportOnGalleryUsage, displayImage } = props;

  const MODAL_QUERY_KEY = 'modal';
  const MODAL_QUERY_VALUE = 'gallery';

  const { addQueries, checkIfQueryIsPresent, removeQueries } = useQueryParams();

  const { back } = useRouter();
  const isGalleryOpen = checkIfQueryIsPresent(
    MODAL_QUERY_KEY,
    MODAL_QUERY_VALUE,
  );

  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  const [initialIndexOverride, setInitialIndexOverride] = useState<
    number | null
  >(null);

  // The image the inline carousel should restore to once the lightbox closes.
  const [restoreModalIndex, setRestoreModalIndex] = useState<number | null>(
    null,
  );
  // The lightbox's live slide index, reported on every change. Held in a ref so
  // it survives any close path — crucially the device/browser back gesture,
  // which pops the route and unmounts the modal without running closeGallery.
  const lastModalIndexRef = useRef<number | null>(null);
  const reportModalIndex = useCallback((index: number) => {
    lastModalIndexRef.current = index;
  }, []);

  // Restore on the open → closed transition rather than from closeGallery, so
  // it fires no matter how the lightbox was dismissed (X, Escape, overlay, or
  // back gesture). The open-side reset lives in openGallery so it runs before
  // the modal mounts and reports its initial index (avoiding a wipe of that
  // first report by this effect).
  const wasGalleryOpenRef = useRef(isGalleryOpen);
  useEffect(() => {
    const wasOpen = wasGalleryOpenRef.current;
    wasGalleryOpenRef.current = isGalleryOpen;

    if (wasOpen && !isGalleryOpen) {
      setRestoreModalIndex(lastModalIndexRef.current);
    }
  }, [isGalleryOpen]);

  useEffect(() => {
    // On load of this component we preload the first image in
    // the lightbox so when the user opens it they get an instant load
    const firstImageOfLightboxToPreload = new Image();
    // One of two optional parameters will always be available to set as src
    firstImageOfLightboxToPreload.src = displayImage!;
  }, [displayImage]);

  const openGallery = (data: GalleryOpenData, initialIndex?: number) => {
    addQueries({ modal: MODAL_QUERY_VALUE });
    setSessionStorageItem(MODAL_QUERY_KEY, MODAL_QUERY_VALUE);
    setInitialIndexOverride(initialIndex ?? null);
    // Clear any stale restore + last index before the modal mounts.
    setRestoreModalIndex(null);
    lastModalIndexRef.current = null;
    reportOnGalleryUsage && reportOnGalleryUsage({ event: 'OPEN', data });
  };

  const closeGallery = (usageData: GalleryUsageData) => {
    const modalQueryKey = getSessionStorageItem(MODAL_QUERY_KEY);

    /*
     * If the session storage item set when opening the modal is present, this indicates that the user
     * opened the modal rather than navigating directly to the gallery, in which case it is safe to
     * call the back method because they will be brought back to the ad details page.
     *
     * Otherwise, the user navigated directly to ad details in which case we remove the query manually to close the modal.
     */
    if (modalQueryKey === MODAL_QUERY_VALUE) {
      back();
      removeSessionStorageItem(MODAL_QUERY_KEY);
    } else {
      removeQueries([MODAL_QUERY_KEY]);
    }
    {
      reportOnGalleryUsage &&
        reportOnGalleryUsage({ event: 'CLOSE', data: usageData });
    }
    setInitialIndexOverride(null);
  };

  return {
    openGallery,
    closeGallery,
    isGalleryOpen,
    initialIndexOverride,
    restoreModalIndex,
    reportModalIndex,
  };
};

export { useGalleryOnPage };
